import { Injectable } from '@angular/core';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { SseResponseEnum } from '../utils/sse/sse-responses';
import { IModalGlobal } from './modal-service.service';
import {
  ISnackMsg,
  SnackTypeMsg,
} from '../../app/model/snackmsg/snackmsg.model';
import { TranslateService } from '@ngx-translate/core';
import { IDataUserLogged } from '../model/http/users.model';
import { CVE_PORTAL } from '../constants/cve_portal';
import { DEFAULT_ACTIONS } from '../constants/default-actions';
import { Router } from '@angular/router';
import { IActionPermissions } from '../model/http/menu.model';
import { FlowType, isResetFlowType } from '../constants/flow-types';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  isLoading = new Subject<boolean>();

  private readonly generalNotificationMessage = new Subject<ISnackMsg>();
  private readonly sse$ = new Subject<SseResponseEnum>();
  private readonly screenWidth$ = new BehaviorSubject<number>(
    window.innerWidth,
  );
  private readonly screenHeight$ = new BehaviorSubject<number>(
    window.innerHeight,
  );
  private readonly globalModal$ = new Subject<IModalGlobal>();
  private readonly blockScreen$ = new Subject<Object>();
  private readonly actionsService$ = new BehaviorSubject<Object>(
    DEFAULT_ACTIONS,
  );
  private readonly notificationSse$ = new Subject<boolean>();

  dataUser: IDataUserLogged = {
    userName: '',
    userId: 0,
    shortName: '',
    lettersName: '',
    userEmail: '',
    fullName: '',
    rols: [],
  };
  private readonly dataUser$ = new BehaviorSubject<IDataUserLogged>(
    this.dataUser,
  );

  constructor(
    private readonly translateService: TranslateService,
    private readonly router: Router,
  ) {}

  setIsLoading(loading: boolean): void {
    this.isLoading.next(loading);
  }

  getIsLoading(): Observable<boolean> {
    return this.isLoading.asObservable();
  }

  setDataUser(dataUser: IDataUserLogged): void {
    this.dataUser$.next(dataUser);
  }

  getDataUser(): Observable<IDataUserLogged> {
    return this.dataUser$.asObservable();
  }

  getGeneralNotificationMessage(): Observable<ISnackMsg> {
    return this.generalNotificationMessage.asObservable();
  }

  setGeneralNotificationMessage(data: ISnackMsg): void {
    this.generalNotificationMessage.next(data);
  }

  getToken(): string {
    return sessionStorage.getItem('token') || '';
  }

  setToken(token: string): void {
    sessionStorage.setItem('token', token);
  }

  removeToken(): void {
    sessionStorage.removeItem('token');
  }

  userIsAuthenticated(): boolean {
    return Boolean(sessionStorage.getItem('token'));
  }

  setSse$(sseResponse: SseResponseEnum): void {
    this.sse$.next(sseResponse);
  }

  getSse$(): Observable<any> {
    return this.sse$.asObservable();
  }

  setScreenWidth$(width: number): void {
    this.screenWidth$.next(width);
  }

  getScreenWidth$(): Observable<number> {
    return this.screenWidth$.asObservable();
  }

  setScreenHeight$(height: number): void {
    this.screenHeight$.next(height);
  }

  getScreenHeight$(): Observable<number> {
    return this.screenHeight$.asObservable();
  }

  setModalGlobal$(data: IModalGlobal) {
    this.globalModal$.next(data);
  }

  getModalGlobal$() {
    return this.globalModal$.asObservable();
  }

  getPortalType(paramPortal: string) {
    const portalType: { [key: string]: string } = {
      admin: this.translateService.instant('portal.admin'),
      web: this.translateService.instant('portal.web'),
      mobile: this.translateService.instant('portal.mobile'),
      student: this.translateService.instant('portal.students'),
    };

    return portalType[paramPortal];
  }

  getPortalCve(paramPortal: string) {
    const portalCve: { [key: string]: string } = {
      admin: CVE_PORTAL.Admin,
      web: CVE_PORTAL.Web,
      mobile: CVE_PORTAL.Mobile,
      student: CVE_PORTAL.Student,
    };

    return portalCve[paramPortal];
  }

  userHasRole(id: number): boolean {
    const role = this.dataUser.rols.find((rolUser) => rolUser.id === id);
    return role !== undefined;
  }

  setBlocklScreen(dataBlocked: Object): void {
    this.blockScreen$.next(dataBlocked);
  }

  getBlockScreen(): Observable<Object> {
    return this.blockScreen$.asObservable();
  }

  setActionsService$(actions: any) {
    this.actionsService$.next(actions);
  }

  getActionsService$(): Observable<any> {
    return this.actionsService$.asObservable();
  }

  setNotificationSse(flag: boolean) {
    this.notificationSse$.next(flag);
  }

  getNotificationSse(): Observable<any> {
    return this.notificationSse$.asObservable();
  }

  checkNotFoundRegisters(options: any[], word: string) {
    if (options.length === 0)
      this.setGeneralNotificationMessage({
        msg: `${this.translateService.instant('not-find-exits')} ${word}`,
        type: SnackTypeMsg.Error,
      });
  }

  copyValueAlert() {
    this.setGeneralNotificationMessage({
      msg: this.translateService.instant('copied'),
      type: SnackTypeMsg.Info,
    });
  }

  validatePermissionOrFix(
    flowType: FlowType,
    actionsPermissions: IActionPermissions,
    fallbackRoute: string,
  ): FlowType {
    const editFix = this.resolveEditFlow(
      flowType,
      actionsPermissions,
      fallbackRoute,
    );
    if (editFix !== null) return editFix;
    if (this.isCreateOrDuplicateWithoutCrear(flowType, actionsPermissions)) {
      this.navigateAndShowError(fallbackRoute);
      return flowType;
    }
    if (this.isDetailWithoutLectura(flowType, actionsPermissions)) {
      this.navigateAndShowError(fallbackRoute);
      return flowType;
    }
    return flowType;
  }

  private resolveEditFlow(
    flowType: FlowType,
    actionsPermissions: IActionPermissions,
    fallbackRoute: string,
  ): FlowType | null {
    if (!this.isEditWithoutEditar(flowType, actionsPermissions)) return null;
    if (!actionsPermissions.Lectura) {
      this.navigateAndShowError(fallbackRoute);
      return flowType;
    }
    return 'detail';
  }

  private isEditWithoutEditar(
    flowType: FlowType,
    actionsPermissions: IActionPermissions,
  ): boolean {
    return flowType === 'edit' && !actionsPermissions.Editar;
  }

  private isCreateOrDuplicateWithoutCrear(
    flowType: FlowType,
    actionsPermissions: IActionPermissions,
  ): boolean {
    return isResetFlowType(flowType) && !actionsPermissions.Crear;
  }

  private isDetailWithoutLectura(
    flowType: FlowType,
    actionsPermissions: IActionPermissions,
  ): boolean {
    return flowType === 'detail' && !actionsPermissions.Lectura;
  }

  navigateAndShowError(route: string): void {
    this.router?.navigateByUrl(route);
    this.setGeneralNotificationMessage({
      msg: this.translateService.instant('not-permissions'),
      type: SnackTypeMsg.Warning,
    });
  }
}
